<script>
import BotoesNavegacao from '@/components/BotoesNavegacao.vue'
import { CONCESSIONARIAS } from '@/data/concessionarias'
import { buscarCep } from '@/utils/viacep'

export default {
  components: { BotoesNavegacao },

  props: {
    form: { type: Object, required: true },
    concessionariaNaoIdentificada: { type: String, default: '' },
  },

  data() {
    return {
      concessionarias: CONCESSIONARIAS,
      buscandoCep: false,
    }
  },

  watch: {
    'form.cep'(v) {
      const c = (v || '').replace(/\D/g, '')
      if (c.length === 8) this.lookupCep(c)
    },
  },

  methods: {
    async lookupCep(c) {
      this.buscandoCep = true
      try {
        const r = await buscarCep(c)
        if (r) {
          this.form.logradouro = r.logradouro || this.form.logradouro
          this.form.bairro     = r.bairro || this.form.bairro
          this.form.cidade     = r.cidade || this.form.cidade
          this.form.estado     = r.estado || this.form.estado
        }
      } finally {
        this.buscandoCep = false
      }
    },
    // Número do endereço aceita só dígitos: remove as letras digitadas e avisa
    filtrarNumero(event) {
      const original = event.target.value
      const digits   = original.replace(/\D/g, '')

      if (digits !== original) {
        this.$toast.error('O campo número aceita apenas números. Se o endereço não tiver número, marque "Sem número".')
      }

      this.form.numero   = digits
      event.target.value = digits
    },
    toggleSemNumero(checked) {
      this.form.numero = checked ? 'S/N' : ''
    },
    avancar() {
      if (!this.form.concessionaria_id) {
        this.$toast.error('Selecione sua concessionária de energia.')
        return
      }
      if (!this.form.numero_instalacao) {
        this.$toast.error('Informe o número de instalação (ou unidade consumidora) da sua conta.')
        return
      }
      if (!this.form.kwh_avg || isNaN(Number(this.form.kwh_avg))) {
        this.$toast.error('Informe o consumo médio em kWh.')
        return
      }
      const cep = (this.form.cep || '').replace(/\D/g, '')
      if (cep.length !== 8) {
        this.$toast.error('Informe um CEP válido (8 dígitos).')
        return
      }
      if (!this.form.logradouro || !this.form.numero || !this.form.cidade || !this.form.estado) {
        this.$toast.error('Preencha endereço, número, cidade e UF.')
        return
      }
      this.$emit('avancar')
    },
  },
}
</script>

<template>
  <div>
    <div v-if="concessionariaNaoIdentificada" class="alert alert--warn">
      Não conseguimos identificar a concessionária <strong>{{ concessionariaNaoIdentificada }}</strong> automaticamente. Selecione na lista abaixo.
    </div>

    <div class="form-row">
      <label class="field-label">Concessionária</label>
      <select v-model="form.concessionaria_id" class="select">
        <option value="">Selecione...</option>
        <option v-for="c in concessionarias" :key="c.id" :value="c.id">{{ c.nome }}</option>
      </select>
    </div>

    <div class="form-row">
      <label class="field-label">Nº de instalação / UC</label>
      <input v-model="form.numero_instalacao" v-mask="'#.###.###.###-##'" type="text" class="input" placeholder="Ex: 3.716.000.011-17" inputmode="numeric" />
    </div>

    <div class="form-row">
      <label class="field-label">Tipo de instalação</label>
      <select v-model="form.tipo_fase" class="select">
        <option value="">Não sei</option>
        <option value="monofasico">Monofásico</option>
        <option value="bifasico">Bifásico</option>
        <option value="trifasico">Trifásico</option>
      </select>
    </div>

    <div class="form-row">
      <label class="field-label">Consumo médio (kWh)</label>
      <input v-model="form.kwh_avg" type="number" step="0.01" min="0" class="input" placeholder="Ex: 250" inputmode="numeric" />
    </div>

    <div class="form-row">
      <label class="field-label">Mês de referência</label>
      <input v-model="form.mes_referencia" type="month" class="input" />
    </div>

    <div class="form-row">
      <label class="field-label">Valor da fatura (R$)</label>
      <input v-model="form.valor_fatura" type="number" step="0.01" min="0" class="input" placeholder="Ex: 250.00" inputmode="decimal" />
    </div>

    <h3 class="section-title" style="font-size: 1.05rem; margin-top: 24px;">Endereço da instalação</h3>

    <div class="form-row">
      <label class="field-label">CEP</label>
      <input
        v-model="form.cep"
        v-mask="'#####-###'"
        type="text"
        class="input"
        placeholder="00000-000"
        inputmode="numeric"
      />
      <div v-if="buscandoCep" class="field-hint">Buscando endereço...</div>
    </div>

    <div class="form-row">
      <label class="field-label">Logradouro</label>
      <input v-model="form.logradouro" type="text" class="input" placeholder="Rua / Avenida" />
    </div>

    <div class="form-row">
      <label class="field-label">Número</label>
      <input :value="form.numero" :disabled="form.numero === 'S/N'" type="text" class="input" placeholder="123" inputmode="numeric" @input="filtrarNumero" />
      <label class="field-check">
        <input type="checkbox" :checked="form.numero === 'S/N'" @change="toggleSemNumero($event.target.checked)" />
        <span>Sem número</span>
      </label>
    </div>

    <div class="form-row">
      <label class="field-label">Complemento</label>
      <input v-model="form.complemento" type="text" class="input" placeholder="Apto / Bloco" />
    </div>

    <div class="form-row">
      <label class="field-label">Bairro</label>
      <input v-model="form.bairro" type="text" class="input" />
    </div>

    <div class="form-row">
      <label class="field-label">Cidade</label>
      <input v-model="form.cidade" type="text" class="input" />
    </div>

    <div class="form-row">
      <label class="field-label">UF</label>
      <input v-model="form.estado" type="text" class="input" maxlength="2" placeholder="SP" />
    </div>

    <div class="form-row">
      <label class="field-label">Observações</label>
      <textarea v-model="form.obs_instalacao" class="textarea" rows="2" placeholder="Algo que você queira nos contar sobre essa instalação?"></textarea>
    </div>

    <BotoesNavegacao @voltar="$emit('voltar')" @avancar="avancar" />
  </div>
</template>
